import { useState, useEffect } from "react";
import * as tableUtils from "../utils/tableUtils.js";
import { useExportData } from "./useExportData.jsx";
import { backendGet } from "../../../services/dataService.js";
import { SEARCH_WORD_KEY } from "../utils/tableUtils.js";

const deepEqual = (a, b) => {
  if (a === b) return true;

  if (
    typeof a !== "object" ||
    typeof b !== "object" ||
    a == null ||
    b == null
  ) {
    return false;
  }

  const keysA = Object.keys(a);
  const keysB = Object.keys(b);

  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) {
      return false;
    }
  }

  return true;
};

const readStoredPageSize = (storageKey) => {
  if (typeof window === "undefined" || !storageKey) {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const value = Number(parsed?.rows_per_page);
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (e) {
    return null;
  }
};

export const useMultiTableData = (tablesConfig, enqueueSnackbar) => {
  const initialTableStates = {
    rows: [],
    isLoading: false,
    hasLoaded: false,
    pageIndex: 0,
    pageSize: 10,
    totalRows: 0,
    filters: [],
    sorting: [],
    searchword: "",
    shouldFetch: false,
  };

  const [tableStates, setTableStates] = useState(
    tablesConfig.reduce((acc, table) => {
      const pageSize =
        table.pageSize === null
          ? null
          : readStoredPageSize(table.storageKey) ||
            table.pageSize ||
            initialTableStates.pageSize;

      acc[table.key] = {
        ...initialTableStates,
        sorting: table.sorting || initialTableStates.sorting,
        pageSize,
        pageIndex: table.pageIndex || initialTableStates.pageIndex,
        filters: table.filters || initialTableStates.filters,
        searchword: table.searchword || initialTableStates.searchword,
      };

      return acc;
    }, {})
  );

  const [tableExportStates, setTableExportStates] = useState(
    tablesConfig.reduce((acc, table) => {
      acc[table.key] = {
        isExporting: false,
      };
      return acc;
    }, {})
  );

  useEffect(() => {
    setTableStates((prev) => {
      const next = { ...prev };

      tablesConfig.forEach((table) => {
        const currentState = next[table.key];
        const desiredPageSize =
          table.pageSize === null
            ? null
            : readStoredPageSize(table.storageKey) ||
              table.pageSize ||
              initialTableStates.pageSize;
        const desiredSorting = table.sorting || initialTableStates.sorting;

        if (!currentState) {
          next[table.key] = {
            ...initialTableStates,
            pageSize: desiredPageSize,
            sorting: desiredSorting,
            shouldFetch: true,
          };
          return;
        }

        // Check if pageSize changed in config
        // (but preserve user's current pageSize if config didn't explicitly change it)
        const configPageSizeChanged =
          table.pageSize && currentState.pageSize !== desiredPageSize;

        if (configPageSizeChanged) {
          next[table.key] = {
            ...currentState,
            pageSize: desiredPageSize,
            pageIndex: 0,
            shouldFetch: true,
          };
        }
      });

      return next;
    });

    setTableExportStates((prev) => {
      const next = { ...prev };

      tablesConfig.forEach((table) => {
        if (!next[table.key]) {
          next[table.key] = {
            isExporting: false,
          };
        }
      });

      return next;
    });
  }, [tablesConfig]);

  const resetTableState = (key) => {
    setTableStates((prevState) => ({
      ...prevState,
      [key]: {
        ...prevState[key],
        ...initialTableStates,
      },
    }));
  };

  const getParams = (key) => {
    const state = tableStates[key];
    const tableConfig = tablesConfig.find((table) => table.key === key);
    if (!tableConfig) return {};

    const { simple: simpleFilters, complex: complexFilters } =
      tableUtils.transformFilters(state.filters);
    const transformedSort = tableUtils.transformSort(state.sorting);

    const {
      filters: extraFilters = {},
      sort: extraSort = [],
      ...restExtraParams
    } = tableConfig.extraParams || {};

    const finalFilters =
      simpleFilters && Object.keys(simpleFilters).length > 0
        ? { ...simpleFilters, ...extraFilters }
        : { ...extraFilters };

    const finalSort =
      transformedSort && transformedSort.length > 0
        ? transformedSort
        : extraSort;

    const sortSpec = finalSort
      .map(({ field, order }) => `${order === "desc" ? "-" : ""}${field}`)
      .join(",");

    if (state.searchword) {
      finalFilters[SEARCH_WORD_KEY] = state.searchword;
    }

    if (complexFilters && Object.keys(complexFilters).length > 0) {
      finalFilters[tableUtils.COMPLEX_FILTER_KEY] =
        JSON.stringify(complexFilters);
    }

    return {
      ...restExtraParams,
      ...(state.pageSize !== null && { page: state.pageIndex + 1 }),
      ...(state.pageSize !== null && { limit: state.pageSize }),
      filter: finalFilters,
      sort: sortSpec,
      api_version: "v2",
    };
  };

  const fetchData = async (key) => {
    const tableConfig = tablesConfig.find((table) => table.key === key);
    if (!tableConfig) return;

    const state = tableStates[key];
    const params = getParams(key);

    setTableStates((prev) => ({
      ...prev,
      [key]: { ...prev[key], isLoading: true },
    }));

    try {
      await backendGet(tableConfig.route, {
        label: tableConfig.label,
        setIsLoading: (loading) =>
          setTableStates((prev) => ({
            ...prev,
            [key]: { ...prev[key], isLoading: loading },
          })),
        setResult: (res) => {
          const keyData =
            tableConfig.responseKey || tableConfig.label.toLowerCase();
          const data = res?.data || res?.[keyData]?.data || [];
          const meta = res?.meta || res?.[keyData]?.meta || {};
          const nextPageIndex =
            typeof meta.current_page === "number"
              ? Math.max(meta.current_page - 1, 0)
              : state.pageIndex;
          const nextPageSize =
            typeof meta.per_page === "number" ? meta.per_page : state.pageSize;

          setTableStates((prev) => ({
            ...prev,
            [key]: {
              ...prev[key],
              rows: data,
              totalRows: meta.total ?? data.length,
              pageIndex: nextPageIndex,
              pageSize: nextPageSize,
              hasLoaded: true,
            },
          }));

          // Call optional callback from config to sync state externally (e.g., to Recoil)
          // This must be AFTER setTableStates to avoid setState during render
          if (tableConfig.onStateChange) {
            const tableData = {
              data: [...data],
              meta: {
                total: meta.total ?? data.length,
                limit: nextPageSize,
                offset: nextPageIndex,
              },
            };
            tableConfig.onStateChange(tableData);
          }
        },
        enqueueSnackbar,
        options: {
          params,
        },
      });
    } catch (err) {
      console.error(`Fehler beim Laden (${tableConfig.label}):`, err);
      if (enqueueSnackbar) {
        enqueueSnackbar(`${tableConfig.label} konnte nicht geladen werden`, {
          variant: "error",
        });
      }
      setTableStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          hasLoaded: true,
        },
      }));
    } finally {
      setTableStates((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          isLoading: false,
        },
      }));
    }
  };

  const updateTableState = (key, updates) => {
    setTableStates((prev) => {
      const currentState = prev[key];
      const hasChanges = Object.keys(updates).some(
        (updateKey) => !deepEqual(updates[updateKey], currentState[updateKey])
      );

      if (hasChanges) {
        return {
          ...prev,
          [key]: { ...currentState, ...updates, shouldFetch: true },
        };
      }

      return prev;
    });
  };

  const exportData = async (key, data) => {
    const tableConfig = tablesConfig.find((table) => table.key === key);

    if (!tableConfig || !tableConfig.exportRoute) return;

    const route = tableConfig.exportRoute;
    const label = tableConfig.exportFilename;
    const params = getParams(key);

    // Remove pagination params for export
    delete params.page;
    delete params.limit;

    const handleOnExportData = useExportData({
      route: route,
      label: `Export ${label}`,
      params,
      setIsLoading: (loading) =>
        setTableExportStates((prev) => ({
          ...prev,
          [key]: { ...prev[key], isExporting: loading },
        })),
      enqueueSnackbar,
    });

    await handleOnExportData(data);
  };

  useEffect(() => {
    Object.keys(tableStates).forEach((key) => {
      const state = tableStates[key];
      if (state.shouldFetch) {
        fetchData(key);
        setTableStates((prev) => ({
          ...prev,
          [key]: { ...prev[key], shouldFetch: false },
        }));
      }
    });
  }, [tableStates]);

  return {
    tableStates,
    tableExportStates,
    updateTableState,
    resetTableState,
    fetchData,
    exportData,
  };
};
